import { createStore } from 'vuex';
import {
    checkCongressPaymentMethod,
    createCongressSetupIntent,
    saveBillingAddress as saveBillingAddressApi,
    lookupRepresentatives as lookupRepresentativesApi,
    generateCongressLetter,
    reviseCongressLetter,
    sendCongressLetter,
    previewCongressLetter,
} from './api';
import { createDraftPersistencePlugin, clearDraftStorage, createAuthSlice } from '@thinking-of-u/shared';

// Session-storage draft persistence (shared factory).
const STORAGE_KEY = 'congress-draft';
const DRAFT_FIELDS = [
    'currentStep',
    'senderAddress',
    'representatives',
    'selectedRepresentatives',
    'topic',
    'tone',
    'personalNote',
    'letterVersions',
    'selectedVersionIndex',
    'signature',
];

const draftPersistencePlugin = createDraftPersistencePlugin({
    storageKey: STORAGE_KEY,
    fields: DRAFT_FIELDS,
    skipMutations: ['SET_USER', 'SET_AUTH_LOADING', 'SET_LETTER_LOADING', 'SET_REVISION_LOADING', 'SET_CARD_LOADING', 'SET_CARD_INFO'],
});

const authSlice = createAuthSlice({
    checkPaymentMethodApi: checkCongressPaymentMethod,
    createSetupIntentApi: createCongressSetupIntent,
    saveBillingAddressApi: saveBillingAddressApi,
    onSignOut: ({ commit }) => commit('RESET_LETTER'),
});

export default createStore({
    state: {
        ...authSlice.state,

        // Letter composition state
        currentStep: 1, // 1=address, 2=representative, 3=compose, 4=preview, 5=send

        // Address info
        senderAddress: {
            name: '',
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'US'
        },

        // Representative info (populated from Census/Congress APIs)
        representatives: [],
        selectedRepresentatives: [], // Array of selected reps (can be 1-3)

        // Letter content
        topic: '', // Freeform text describing what the constituent wants to say
        tone: 'respectful',
        personalNote: '',

        // Generated letter versions (max 3)
        letterVersions: [], // Array of { letter: string, feedback?: string }
        selectedVersionIndex: 0,
        letterLoading: false,
        revisionLoading: false,

        // Signature
        signature: null, // Base64 data URL of signature image

        // PDF Preview state
        pdfPreview: null, // Base64 PDF data
        pdfPreviewLoading: false,
    },

    getters: {
        ...authSlice.getters,
        fullAddress: state => {
            const addr = state.senderAddress;
            return `${addr.street}, ${addr.city}, ${addr.state} ${addr.postalCode}`;
        },
        // Letter version getters
        currentLetter: state => {
            if (state.letterVersions.length === 0) return null;
            return state.letterVersions[state.selectedVersionIndex]?.letter || null;
        },
        versionCount: state => state.letterVersions.length,
        canRevise: state => state.letterVersions.length > 0 && state.letterVersions.length < 3,
        // Multi-rep getters
        selectedRepCount: state => state.selectedRepresentatives.length,
        hasSelectedReps: state => state.selectedRepresentatives.length > 0,
        isRepSelected: state => rep => state.selectedRepresentatives.some(r => r.id === rep.id),
        allRepsSelected: state => state.representatives.length > 0 &&
            state.selectedRepresentatives.length === state.representatives.length,
        totalLetterPrice: state => state.selectedRepresentatives.length * 129, // cents
        totalLetterPriceFormatted: state => {
            const total = state.selectedRepresentatives.length * 1.29;
            return `$${total.toFixed(2)}`;
        },
        // For backwards compatibility - returns first selected rep
        primaryRepresentative: state => state.selectedRepresentatives[0] || null,
    },

    mutations: {
        ...authSlice.mutations,
        SET_CURRENT_STEP(state, step) {
            state.currentStep = step;
        },
        SET_SENDER_ADDRESS(state, address) {
            state.senderAddress = { ...state.senderAddress, ...address };
        },
        SET_REPRESENTATIVES(state, reps) {
            state.representatives = reps;
        },
        SET_SELECTED_REPRESENTATIVES(state, reps) {
            state.selectedRepresentatives = reps;
        },
        TOGGLE_REPRESENTATIVE(state, rep) {
            const index = state.selectedRepresentatives.findIndex(r => r.id === rep.id);
            if (index === -1) {
                state.selectedRepresentatives.push(rep);
            } else {
                state.selectedRepresentatives.splice(index, 1);
            }
        },
        SELECT_ALL_REPRESENTATIVES(state) {
            state.selectedRepresentatives = [...state.representatives];
        },
        SET_TOPIC(state, topic) {
            state.topic = topic;
        },
        SET_TONE(state, tone) {
            state.tone = tone;
        },
        SET_PERSONAL_NOTE(state, note) {
            state.personalNote = note;
        },
        // Letter version mutations
        ADD_LETTER_VERSION(state, { letter, feedback = null }) {
            state.letterVersions.push({ letter, feedback });
            state.selectedVersionIndex = state.letterVersions.length - 1;
        },
        SET_SELECTED_VERSION(state, index) {
            if (index >= 0 && index < state.letterVersions.length) {
                state.selectedVersionIndex = index;
            }
        },
        CLEAR_LETTER_VERSIONS(state) {
            state.letterVersions = [];
            state.selectedVersionIndex = 0;
        },
        SET_SIGNATURE(state, signatureDataUrl) {
            state.signature = signatureDataUrl;
        },
        SET_LETTER_LOADING(state, loading) {
            state.letterLoading = loading;
        },
        SET_REVISION_LOADING(state, loading) {
            state.revisionLoading = loading;
        },
        SET_PDF_PREVIEW(state, pdfBase64) {
            state.pdfPreview = pdfBase64;
        },
        SET_PDF_PREVIEW_LOADING(state, loading) {
            state.pdfPreviewLoading = loading;
        },
        RESET_LETTER(state) {
            state.currentStep = 1;
            state.topic = '';
            state.tone = 'respectful';
            state.personalNote = '';
            state.letterVersions = [];
            state.selectedVersionIndex = 0;
            state.selectedRepresentatives = [];
            state.signature = null;
            state.pdfPreview = null;
        },
    },

    actions: {
        ...authSlice.actions,
        // Representative lookup
        async lookupRepresentatives({ commit, state }) {
            try {
                const result = await lookupRepresentativesApi({ address: state.senderAddress });
                commit('SET_REPRESENTATIVES', result.data.representatives);
                return result.data.representatives;
            } catch (error) {
                console.error('Representative lookup error:', error);
                throw error;
            }
        },

        // Letter generation
        async generateLetter({ commit, state, getters }) {
            commit('SET_LETTER_LOADING', true);
            commit('CLEAR_LETTER_VERSIONS'); // Clear any previous versions
            try {
                // Use all selected reps for context, but generate one letter
                const result = await generateCongressLetter({
                    representative: getters.primaryRepresentative,
                    allRepresentatives: state.selectedRepresentatives,
                    topic: state.topic,
                    tone: state.tone,
                    personalNote: state.personalNote,
                    senderAddress: state.senderAddress,
                    senderName: state.senderAddress.name || '',
                });
                commit('ADD_LETTER_VERSION', { letter: result.data.letter });
                return result.data.letter;
            } catch (error) {
                console.error('Letter generation error:', error);
                // Check for rate limit error
                if (error.code === 'functions/resource-exhausted') {
                    const rateLimitError = new Error('You\'ve used all your free letter generations. Please add a payment method to continue writing letters.');
                    rateLimitError.isRateLimited = true;
                    throw rateLimitError;
                }
                throw error;
            } finally {
                commit('SET_LETTER_LOADING', false);
            }
        },

        // Letter revision
        async reviseLetter({ commit, state, getters }, feedback) {
            if (!getters.canRevise) {
                throw new Error('Maximum revisions reached');
            }
            commit('SET_REVISION_LOADING', true);
            try {
                const result = await reviseCongressLetter({
                    originalLetter: getters.currentLetter,
                    feedback: feedback,
                    representative: getters.primaryRepresentative,
                    senderName: state.senderAddress.name || '',
                });
                commit('ADD_LETTER_VERSION', { letter: result.data.letter, feedback });
                return result.data.letter;
            } catch (error) {
                console.error('Letter revision error:', error);
                // Check for rate limit error
                if (error.code === 'functions/resource-exhausted') {
                    const rateLimitError = new Error('You\'ve used all your free letter generations. Please add a payment method to continue writing letters.');
                    rateLimitError.isRateLimited = true;
                    throw rateLimitError;
                }
                throw error;
            } finally {
                commit('SET_REVISION_LOADING', false);
            }
        },

        // Send letter to all selected representatives
        async sendLetter({ dispatch, state, getters }) {
            const results = [];
            const errors = [];

            // Send to each selected representative
            for (const rep of state.selectedRepresentatives) {
                try {
                    const result = await sendCongressLetter({
                        letter: getters.currentLetter,
                        representative: rep,
                        senderAddress: state.senderAddress,
                        topic: state.topic,
                        signature: state.signature,
                    });
                    results.push({ rep, success: true, data: result.data });
                } catch (error) {
                    console.error(`Send letter error for ${rep.name}:`, error);
                    errors.push({ rep, error });
                }
            }

            // If all failed, throw error
            if (results.length === 0 && errors.length > 0) {
                throw new Error('Failed to send letters. Please try again.');
            }

            // Clear the draft from session storage
            // Note: Don't reset letter state here - let component show success screen first
            dispatch('clearDraft');

            return {
                sent: results,
                failed: errors,
                totalSent: results.length,
                totalFailed: errors.length,
            };
        },

        // Preview letter PDF (shows preview for primary representative)
        async previewLetter({ commit, state, getters }) {
            commit('SET_PDF_PREVIEW_LOADING', true);
            commit('SET_PDF_PREVIEW', null);
            try {
                const result = await previewCongressLetter({
                    letter: getters.currentLetter,
                    representative: getters.primaryRepresentative,
                    senderAddress: state.senderAddress,
                    signature: state.signature,
                });
                commit('SET_PDF_PREVIEW', result.data.pdfBase64);
                return result.data.pdfBase64;
            } catch (error) {
                console.error('Preview letter error:', error);
                throw error;
            } finally {
                commit('SET_PDF_PREVIEW_LOADING', false);
            }
        },

        // Navigation
        nextStep({ commit, state }) {
            if (state.currentStep < 5) {
                commit('SET_CURRENT_STEP', state.currentStep + 1);
            }
        },
        prevStep({ commit, state }) {
            if (state.currentStep > 1) {
                commit('SET_CURRENT_STEP', state.currentStep - 1);
            }
        },
        goToStep({ commit }, step) {
            commit('SET_CURRENT_STEP', step);
        },

        // Clear draft from session storage (call when letter is sent or user explicitly starts over)
        clearDraft() {
            clearDraftStorage(STORAGE_KEY);
        },
    },

    plugins: [draftPersistencePlugin],
});
